if (!customElements.get('image-feature-cards')) {
	class ImageFeatureCards extends HTMLElement {
		connectedCallback() {
			this.cards = Array.from(this.querySelectorAll('[data-feature-card]'));

			this.onClick = this.onClick.bind(this);
			this.onKeyDown = this.onKeyDown.bind(this);
			this.onBlockToggle = this.onBlockToggle.bind(this);
			this.build = this.build.bind(this);

			this.addEventListener('click', this.onClick);
			this.addEventListener('keydown', this.onKeyDown);
			this.addEventListener('shopify:block:select', this.onBlockToggle);
			this.addEventListener('shopify:block:deselect', this.onBlockToggle);

			// Carousel only on mobile; tablet and desktop keep the static grid.
			this.mobileQuery = window.matchMedia('(max-width: 767px)');
			this.mobileQuery.addEventListener('change', this.build);

			if (typeof Flickity === 'undefined') {
				window.addEventListener('load', this.build, { once: true });
				return;
			}
			this.build();
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.onClick);
			this.removeEventListener('keydown', this.onKeyDown);
			this.removeEventListener('shopify:block:select', this.onBlockToggle);
			this.removeEventListener('shopify:block:deselect', this.onBlockToggle);
			this.mobileQuery.removeEventListener('change', this.build);
			window.removeEventListener('load', this.build);
			this.destroy();
		}

		build() {
			if (typeof Flickity === 'undefined') return;

			if (!this.mobileQuery.matches) {
				this.destroy();
				return;
			}
			if (this.flkty) return;

			this.flkty = new Flickity(this, {
				cellSelector: '[data-feature-card]',
				cellAlign: 'center',
				contain: false,
				wrapAround: this.dataset.loop === 'true' && this.cards.length > 1,
				autoPlay: this.cards.length > 1 && Number(this.dataset.autoplay) > 0 ? Number(this.dataset.autoplay) : false,
				pageDots: this.dataset.dots === 'true' && this.cards.length > 1,
				prevNextButtons: false,
				draggable: this.cards.length > 1,
				rightToLeft: document.dir === 'rtl'
			});

			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(() => this.flkty && this.flkty.resize());
			}
		}

		destroy() {
			if (!this.flkty) return;

			this.flkty.destroy();
			this.flkty = null;
		}

		onClick(event) {
			// Flickity cancels the click that ends a drag.
			if (event.defaultPrevented) return;

			const card = event.target.closest('[data-feature-card]');
			if (!card) return;

			// Mobile carousel: tapping a card other than the current one slides to
			// it, forwards or backwards (shortest way round when looping).
			if (this.flkty && !card.classList.contains('is-selected')) {
				this.flkty.selectCell(card, true);
			}

			const toggle = event.target.closest('[data-feature-card-toggle]');
			if (!toggle) return;

			this.setOpen(card, !card.classList.contains('is-open'));
		}

		onKeyDown(event) {
			if (event.key !== 'Escape') return;

			const card = event.target.closest('[data-feature-card].is-open');
			if (!card) return;

			this.setOpen(card, false);
			card.querySelector('[data-feature-card-toggle]').focus();
		}

		// Theme editor: selecting a block brings its card into view and opens it,
		// so the text can be edited in place.
		onBlockToggle(event) {
			const card = event.target.closest('[data-feature-card]');
			const selected = event.type === 'shopify:block:select';

			if (selected && this.flkty && card) this.flkty.selectCell(card, false, true);
			this.setOpen(card, selected);
		}

		// Only the given card changes; the others keep their own state. The
		// animation itself is pure CSS, driven by the is-open class.
		setOpen(card, open) {
			const toggle = card && card.querySelector('[data-feature-card-toggle]');
			if (!toggle) return;

			card.classList.toggle('is-open', open);
			toggle.setAttribute('aria-expanded', open);
			toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);

			if (!open) card.querySelector('.image-feature-card__text').scrollTop = 0;

			// Autoplay would slide an open card away while it is being read.
			if (this.flkty) {
				if (this.querySelector('[data-feature-card].is-open')) this.flkty.pausePlayer();
				else this.flkty.unpausePlayer();
			}
		}
	}

	customElements.define('image-feature-cards', ImageFeatureCards);
}
